import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from './useDebounce';
import { nibApi } from '@services/api';
import type {
  NIBContributionPreview,
  NIBWagePreview,
  NIBContributionCalculatorInput,
} from '@/types';

interface UseContributionCalculatorReturn {
  preview: NIBContributionPreview | null;
  wagePreview: NIBWagePreview | null;
  isCalculating: boolean;
  error: Error | null;
  calculate: (input: NIBContributionCalculatorInput) => void;
  reset: () => void;
}

export function useContributionCalculator(): UseContributionCalculatorReturn {
  const [input, setInput] = useState<NIBContributionCalculatorInput | null>(
    null
  );
  const [preview, setPreview] = useState<NIBContributionPreview | null>(null);
  const [wagePreview, setWagePreview] = useState<NIBWagePreview | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Debounce the input to avoid excessive API calls
  const debouncedInput = useDebounce(input, 500);

  // Calculate contributions when debounced input changes
  useEffect(() => {
    if (!debouncedInput) {
      setPreview(null);
      setWagePreview(null);
      return;
    }

    const calculateContributions = async () => {
      setIsCalculating(true);
      setError(null);

      try {
        // First get wage preview to calculate insurable_wages
        const wageResult = await nibApi.previewWages({
          category: debouncedInput.category,
          original_wage: debouncedInput.original_wage,
          pay_indicator: debouncedInput.pay_indicator,
          weeks_worked: debouncedInput.weeks,
        });

        // Validate wage result before proceeding
        if (!wageResult || typeof wageResult.capped_wage === 'undefined') {
          console.error('Invalid wage preview result:', wageResult);
          throw new Error('Failed to calculate insurable wages');
        }

        setWagePreview(wageResult);

        // Then use capped_wage as insurable_wages for contribution calculation
        const insurableWages = parseFloat(wageResult.capped_wage);

        const contributionResult = await nibApi.previewContribution({
          category: debouncedInput.category,
          insurable_wages: insurableWages,
          gratuity_wages: debouncedInput.gratuity_wages ?? 0,
        });

        setPreview(contributionResult);
      } catch (err) {
        console.error('Contribution calculation error:', err);
        setError(err instanceof Error ? err : new Error('Failed to calculate'));
        setPreview(null);
        setWagePreview(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateContributions();
  }, [debouncedInput]);

  const calculate = useCallback((newInput: NIBContributionCalculatorInput) => {
    setInput(newInput);
  }, []);

  const reset = useCallback(() => {
    setInput(null);
    setPreview(null);
    setWagePreview(null);
    setError(null);
  }, []);

  return {
    preview,
    wagePreview,
    isCalculating,
    error,
    calculate,
    reset,
  };
}
